import React, { useState, useEffect, useRef } from 'react';
import {
  Compass,
  Cpu,
  Code,
  ShieldCheck,
  RefreshCw,
  Globe,
  Activity,
  Zap,
  Layers,
  Settings,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CircuitBoard,
} from 'lucide-react';
import { SERVICES_DATA } from '../data/simatechData';
import { ServiceItem } from '../types';
import { useCms } from '../context/CmsContext';
import { renderServiceIconById } from '../data/serviceIconsData';


interface ServicesSectionProps {
  onSelectService: (service: ServiceItem) => void;
  onOpenInquiry: (subject?: string) => void;
}

// Curated industrial solid background accents for the right panel (no gradient)
const PANEL_THEMES = [
  'bg-[#06242a]',
  'bg-[#1b7481]',
  'bg-[#0e444e]',
  'bg-[#135763]',
  'bg-[#1a6e7d]',
  'bg-[#082a30]',
  'bg-[#1b6f7e]',
  'bg-[#0d3f48]',
  'bg-[#155b67]',
  'bg-[#06242a]',
];

export const ServicesSection: React.FC<ServicesSectionProps> = ({
  onSelectService,
  onOpenInquiry,
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const { siteData } = useCms();
  const homeData = siteData?.pages?.home;
  const sectionBgColor = homeData?.servicesSectionBgColor || '#ebedf4';
  const sectionBadge = homeData?.servicesBadge || homeData?.servicesSectionBadge || 'Engineering & Technology';
  const sectionHeading = homeData?.servicesHeading || homeData?.servicesSectionHeading || 'Industrial Services';
  const sectionSubheading = homeData?.servicesSubheading || homeData?.servicesSectionSubheading || 'We provide complete industrial automation services from digital engineering to turnkey plant commissioning.';

  // Dynamic typography overrides
  const headingFontFamily = homeData?.servicesHeadingFontFamily || "'Jost', sans-serif";
  const headingFontSize = homeData?.servicesHeadingFontSize;
  const headingFontWeight = homeData?.servicesHeadingFontWeight || '300';
  const headingColor = homeData?.servicesHeadingColor || '#06242a';

  const subheadingFontFamily = homeData?.servicesSubheadingFontFamily || "'Google Sans Flex', sans-serif";
  const subheadingFontSize = homeData?.servicesSubheadingFontSize;
  const subheadingColor = homeData?.servicesSubheadingColor || '#475569';

  // Lower consultation banner overrides
  const bannerHeading = homeData?.servicesConsultationHeading || 'Need a Custom Automation or Drive Retrofit Scope?';
  const bannerSubtitle = homeData?.servicesConsultationSubtitle || 'Connect directly with our senior automation specialists for rapid feasibility analysis, Single-Line Diagrams (SLD), and turnkey FAT/SAT commissioning quotes with zero production disruption.';
  const bannerBtnText = homeData?.servicesConsultationBtnText || 'Request Project Scope';
  const bannerHeadingFontFamily = homeData?.servicesConsultationHeadingFontFamily || "'Jost', sans-serif";
  const bannerHeadingFontSize = homeData?.servicesConsultationHeadingFontSize;
  const bannerHeadingColor = homeData?.servicesConsultationHeadingColor || '#ffffff';

  // Dynamic services list: Centrally synchronized with admin/pages/services/ - All Services Content Manager (CRUD)
  const pageServices = siteData?.pages?.services;
  const rawServices = (pageServices?.servicesList && Array.isArray(pageServices.servicesList) && pageServices.servicesList.length > 0)
    ? pageServices.servicesList
    : (homeData?.servicesList && Array.isArray(homeData.servicesList) && homeData.servicesList.length > 0)
    ? homeData.servicesList
    : SERVICES_DATA;

  const servicesList: ServiceItem[] = rawServices.map((svc: ServiceItem, idx: number) => {
    const defSvc = SERVICES_DATA.find((s) => s.id === svc.id) || (idx < SERVICES_DATA.length ? SERVICES_DATA[idx] : undefined);
    return defSvc ? { ...defSvc, ...svc } : svc;
  });

  const activeService = servicesList[activeIndex] || servicesList[0] || SERVICES_DATA[0];
  const activeTheme = PANEL_THEMES[activeIndex % PANEL_THEMES.length];

  const totalServices = servicesList.length;
  const prevIndex = (activeIndex - 1 + totalServices) % totalServices;
  const nextIndex = (activeIndex + 1) % totalServices;

  const prevService = servicesList[prevIndex];
  const nextService = servicesList[nextIndex];

  const handlePrev = () => setActiveIndex(prevIndex);
  const handleNext = () => setActiveIndex(nextIndex);

  const getServiceIcon = (iconName: string, className = 'w-6 h-6') => {
    return renderServiceIconById(iconName, {
      className: `${className} stroke-[1] transition-transform duration-500 ease-out group-hover:scale-115`,
      strokeWidth: 1,
    });
  };

  // Split services into two balanced columns for the left numbered grid
  const midPoint = Math.ceil(servicesList.length / 2);
  const leftColumnServices = servicesList.slice(0, midPoint);
  const rightColumnServices = servicesList.slice(midPoint);

  return (
    <section
      id="services"
      ref={sectionRef}
      className="pt-16 sm:pt-20 lg:pt-24 pb-0 relative border-t border-[#1b7481]/15"
      style={{ backgroundColor: sectionBgColor }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* ========================================================================= */}
        {/* DRIBBLE REFERENCE ARRANGEMENT CARD CONTAINER                              */}
        {/* ========================================================================= */}
        <div
          className={`bg-white rounded-[28px] sm:rounded-[36px] lg:rounded-[40px] overflow-hidden shadow-[0_25px_70px_rgba(6,36,42,0.08)] border border-[#06242a]/10 grid grid-cols-1 lg:grid-cols-12 min-h-[640px] items-stretch transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-[0.98]'
          }`}
        >
          
          {/* ----------------------------------------------------------------------- */}
          {/* LEFT SECTION (White Background ~62% Width)                             */}
          {/* ----------------------------------------------------------------------- */}
          <div className="lg:col-span-7 xl:col-span-7 p-7 sm:p-10 lg:p-14 flex flex-col justify-between bg-white">
            
            {/* Top Area: Label, Display Heading & Subtitle */}
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#1b7481] font-semibold mb-3 sm:mb-3.5">
                {sectionBadge}
              </div>

              <h2
                className="text-xl sm:text-2xl md:text-3xl lg:text-[32px] font-light tracking-tight leading-[1.25] mb-3.5 sm:mb-4"
                style={{
                  fontFamily: headingFontFamily,
                  fontSize: headingFontSize || undefined,
                  fontWeight: headingFontWeight,
                  color: headingColor,
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: sectionHeading }} />
              </h2>
              <div className="h-[1px] w-16 bg-gradient-to-r from-[#1b7481] to-[#38c9de] mb-5" />

              <p
                className="text-sm sm:text-[15px] font-light leading-relaxed max-w-xl mb-8 sm:mb-10 lg:mb-12"
                style={{
                  fontFamily: subheadingFontFamily,
                  fontSize: subheadingFontSize || undefined,
                  color: subheadingColor,
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: sectionSubheading }} />
              </p>
            </div>

            {/* Middle Area: 2-Column Services List with Sleek Line Icons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 sm:gap-y-5 pt-6 border-t border-slate-100">
              
              {/* Column 1 (01 - 05) */}
              <div className="flex flex-col gap-3 sm:gap-3.5">
                {leftColumnServices.map((service, idx) => {
                  const globalIdx = idx;
                  const isActive = activeIndex === globalIdx;

                  return (
                    <div
                      key={service.id}
                      onClick={() => setActiveIndex(globalIdx)}
                      className={`cursor-pointer group flex items-center gap-3.5 sm:gap-4 select-none transition-all py-2 px-2.5 rounded-xl ${
                        isActive ? 'bg-[#eef7f9]' : 'hover:bg-slate-50'
                      }`}
                    >
                      {/* Sleek Line Icon (Ultra-fine 1px stroke & hover animation) */}
                      {getServiceIcon(
                        service.iconName,
                        `w-6 h-6 sm:w-7 sm:h-7 shrink-0 transition-all duration-300 stroke-[1] ${
                          isActive
                            ? 'text-[#1b7481] scale-110 drop-shadow-[0_0_8px_rgba(27,116,129,0.35)]'
                            : 'text-slate-400 group-hover:text-[#1b7481] group-hover:drop-shadow-[0_0_6px_rgba(27,116,129,0.25)]'
                        }`
                      )}

                      {/* Service Title */}
                      <span
                        className={`text-[16px] leading-snug font-['Jost',sans-serif] transition-all ${
                          isActive
                            ? 'text-[#1b7481] font-medium translate-x-0.5'
                            : 'text-[#06242a] font-normal group-hover:text-[#1b7481]'
                        }`}
                      >
                        {service.title}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Column 2 (06 - 10) */}
              <div className="flex flex-col gap-3 sm:gap-3.5">
                {rightColumnServices.map((service, idx) => {
                  const globalIdx = midPoint + idx;
                  const isActive = activeIndex === globalIdx;

                  return (
                    <div
                      key={service.id}
                      onClick={() => setActiveIndex(globalIdx)}
                      className={`cursor-pointer group flex items-center gap-3.5 sm:gap-4 select-none transition-all py-2 px-2.5 rounded-xl ${
                        isActive ? 'bg-[#eef7f9]' : 'hover:bg-slate-50'
                      }`}
                    >
                      {/* Sleek Line Icon (Ultra-fine 1px stroke & hover animation) */}
                      {getServiceIcon(
                        service.iconName,
                        `w-6 h-6 sm:w-7 sm:h-7 shrink-0 transition-all duration-300 stroke-[1] ${
                          isActive
                            ? 'text-[#1b7481] scale-110 drop-shadow-[0_0_8px_rgba(27,116,129,0.35)]'
                            : 'text-slate-400 group-hover:text-[#1b7481] group-hover:drop-shadow-[0_0_6px_rgba(27,116,129,0.25)]'
                        }`
                      )}

                      {/* Service Title */}
                      <span
                        className={`text-[16px] leading-snug font-['Jost',sans-serif] transition-all ${
                          isActive
                            ? 'text-[#1b7481] font-medium translate-x-0.5'
                            : 'text-[#06242a] font-normal group-hover:text-[#1b7481]'
                        }`}
                      >
                        {service.title}
                      </span>
                    </div>
                  );
                })}
              </div>

            </div>

          </div>

          {/* ----------------------------------------------------------------------- */}
          {/* RIGHT SECTION (Featured Colored Hero Panel ~38% Width)                  */}
          {/* ----------------------------------------------------------------------- */}
          <div
            className={`lg:col-span-5 xl:col-span-5 relative p-7 sm:p-10 lg:p-12 flex flex-col justify-between text-white transition-all duration-500 overflow-hidden shadow-inner ${activeTheme}`}
          >
            {/* Top / Middle: Sleek Vector Icon and Heading in One Single Line */}
            <div className="relative z-10">
              <div className="mb-6">
                
                {/* Icon & Heading in One Line */}
                <div className="flex items-center gap-3.5 sm:gap-4 mb-4">
                  <div className="shrink-0 text-white drop-shadow-[0_0_12px_rgba(255,255,255,0.45)]">
                    {getServiceIcon(activeService.iconName, 'w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 stroke-[1] text-white')}
                  </div>

                  <h3 className="text-xl sm:text-2xl lg:text-[28px] xl:text-[30px] font-light text-white tracking-tight leading-tight font-['Jost',sans-serif] whitespace-nowrap">
                    {activeService.title}
                  </h3>
                </div>

                <p className="text-xs sm:text-sm text-slate-200 font-light leading-relaxed mb-6">
                  {activeService.scope}
                </p>

                {/* 3 Key Deliverables Checkpoints */}
                <div className="space-y-3 pt-5 border-t border-white/15 mb-7">
                  {activeService.keyFeatures.slice(0, 3).map((feat, fIdx) => (
                    <div key={fIdx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-[#38c9de] shrink-0 mt-0.5" />
                      <p className="text-[15px] text-slate-100 font-light leading-relaxed m-0 p-0">
                        {feat}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Direct Action Buttons */}
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => onOpenInquiry(`Inquiry for ${activeService.title}`)}
                    className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 text-white border border-white px-5 py-2.5 text-xs font-mono tracking-wider uppercase transition-all cursor-pointer"
                  >
                    <span>Request Scope</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onSelectService(activeService)}
                    className="inline-flex items-center gap-1.5 bg-transparent hover:bg-white/10 text-white border border-white px-4 py-2.5 text-xs font-mono tracking-wider uppercase transition-all cursor-pointer"
                  >
                    <span>Specifications</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom: Clean Arrow Navigation Bar (Without Surrounding Text) */}
            <div className="relative z-10 pt-6 border-t border-white/20 flex items-center justify-between select-none">
              
              {/* Active Service Index Telemetry */}
              <span className="text-xs font-mono tracking-widest text-white/70 uppercase">
                {activeService.number} / {totalServices < 10 ? `0${totalServices}` : totalServices}
              </span>

              {/* Interactive Prev/Next Arrow Navigation Buttons */}
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="w-10 h-10 rounded-none bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center text-white transition-all hover:scale-110 cursor-pointer shadow-sm"
                  title="Previous service"
                  aria-label="Previous service"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={handleNext}
                  className="w-10 h-10 rounded-none bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center text-white transition-all hover:scale-110 cursor-pointer shadow-sm"
                  title="Next service"
                  aria-label="Next service"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

            </div>

          </div>

        </div>

      </div>

      {/* ========================================================================= */}
      {/* 2. HIGH-TECH INDUSTRIAL CONSULTATION BANNER (100% FULL-WIDTH EDGE-TO-EDGE)*/}
      {/* ========================================================================= */}
      <div className={`mt-16 sm:mt-20 lg:mt-24 w-full relative overflow-hidden bg-gradient-to-r from-[#031417] via-[#06242a] to-[#04171b] border-y border-[#38c9de]/35 py-12 sm:py-16 shadow-[0_20px_60px_rgba(6,36,42,0.45)] transition-all duration-700 ease-out delay-200 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}>
        
        {/* Static Cyber Dotted Pattern Overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-25"
          style={{
            backgroundImage: 'radial-gradient(circle, #38c9de 1.25px, transparent 1.25px)',
            backgroundSize: '24px 24px',
          }}
        />

        {/* Cinematic Slow-Motion Glowing Light Orbs in Background */}
        <div className="pointer-events-none absolute -top-28 -right-28 w-[480px] h-[480px] bg-[#38c9de] rounded-full blur-[110px] animate-cinematic-glow-1" />
        <div className="pointer-events-none absolute -bottom-28 -left-28 w-[520px] h-[520px] bg-[#1b7481] rounded-full blur-[120px] animate-cinematic-glow-2" />
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[320px] bg-[#6ee7b7] rounded-full blur-[130px] animate-cinematic-glow-3" />
        
        {/* Inner Content Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          
          {/* Left Column: Heading & Scope */}
          <div className="lg:col-span-8 flex flex-col items-start text-left">
            <h3
              className="text-2xl sm:text-3xl lg:text-[32px] font-light tracking-tight leading-snug mb-3"
              style={{
                fontFamily: bannerHeadingFontFamily,
                fontSize: bannerHeadingFontSize || undefined,
                color: bannerHeadingColor,
              }}
            >
              {bannerHeading.includes('Drive Retrofit Scope?') ? (
                <>
                  Need a Custom Automation or <br className="hidden sm:inline" />
                  <span className="font-normal text-transparent bg-clip-text bg-gradient-to-r from-[#38c9de] via-[#6ee7b7] to-[#38c9de]">
                    Drive Retrofit Scope?
                  </span>
                </>
              ) : (
                <span dangerouslySetInnerHTML={{ __html: bannerHeading }} />
              )}
            </h3>

            <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed max-w-2xl">
              <span dangerouslySetInnerHTML={{ __html: bannerSubtitle }} />
            </p>
          </div>

          {/* Right Column: High-Converting Action Button (1px White Border with White Text) */}
          <div className="lg:col-span-4 flex justify-start lg:justify-end w-full">
            <button
              type="button"
              onClick={() => onOpenInquiry('Custom Engineering & Drive Scope')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-transparent hover:bg-white/10 text-white border border-white px-8 py-3.5 text-xs font-mono tracking-wider uppercase transition-all hover:scale-[1.02] cursor-pointer group shadow-sm"
            >
              <span>{bannerBtnText}</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
